import { fetchRequest } from '@growth/fetch-request';
import { generateVid, visitorIdBrowser } from '@growth/visitor-id/browser';
import { getBrowserDetails } from '@growth/user-agents';
import { removeItem, getItem, setItem } from '@growth/cookies';
import { removeEmptyValues } from '@growth/object-utils';
import { getBaseHostName, isOnClient } from '@growth/ssr-utils';
import { create } from 'zustand';
import { DEFAULT_STATE, fetchSetter } from '@growth/store-helpers';
import { promiseDelay } from '@growth/dom-utils';
import { epocToDate } from '@growth/date-utils';
import { docLocalStorage } from '@growth/browser-storage';

var PROD_HOST = "//userservice.goibibo.com";
var STAGING_HOST = "//newprodpp.goibibo.com/moxy";
var PATHS = {
  dweb: "ext/web/desktop",
  mweb: "ext/web/pwa"
};
var getApiUrls = function getApiUrls(p) {
  var urlParams = new URLSearchParams(window.location.search);
  var isStaging = urlParams.get('gi_usrsrvc_staging') === 'true';
  var HOST = isStaging ? STAGING_HOST : PROD_HOST;
  var config = {
    USER_DETAILS: HOST + "/" + PATHS[p] + "/user/details",
    LOGOUT: HOST + "/" + PATHS[p] + "/user/logout",
    MOBILE_DETAILS: HOST + "/" + PATHS[p] + "/send/token/OTP_IS_REG",
    SEND_EMAIL_OTP: HOST + "/" + PATHS[p] + "/send/token/OTP_EMAIL",
    LOGIN: HOST + "/" + PATHS[p] + "/login",
    SIGN_UP: HOST + "/" + PATHS[p] + "/verify/token/SIGNUP_OTP",
    CREATE_NEW_USER: HOST + "/" + PATHS[p] + "/user/create/false",
    UPDATE_PROFILE: HOST + "/" + PATHS[p] + "/user/update",
    GET_EMAIL_TOKEN: HOST + "/" + PATHS[p] + "/send/token/EMAIL",
    VERIFY_EMAIL: HOST + "/" + PATHS[p] + "/verify/token/EMAIL",
    VERIFY_EMAIL_OTP: HOST + "/" + PATHS[p] + "/verify/token",
    UPDATE_PROFILE_PIC: HOST + "/" + PATHS[p] + "/user/upload/imageclass/displaypic",
    REDIRECT_TO_APP: HOST + "/" + PATHS[p] + "/UserService/send/token/LINK",
    DELETE_OTP: HOST + "/" + PATHS[p] + "/send/token/OTP_DELETE",
    SOCIAL_LOGIN: HOST + "/" + PATHS[p] + "/appSocialLogin",
    JWT_TOKEN: HOST + "/" + PATHS[p] + "/jwt/loginToken",
    RECAPTCHA_VERIFY: HOST + "/" + PATHS[p] + "/login/orchestrator",
    LOGOUT_DEVICE: HOST + "/" + PATHS[p] + "/user/logout",
    MULTIPLE_LOGINS: HOST + "/" + PATHS[p] + "/user/activeSessions",
    PREFILL_DETAILS: HOST + "/" + PATHS[p] + "/prefill/login/details"
  };
  return config;
};

var GI_TOKEN_NAME = 'OAUTH-GOIBIBO';
var GA_TOKEN = 'gapauthtoken';
var GI_UUID = 'gitokenid';
var GI_SESSIONID = 'sessionid';
var clearUserTokens = function clearUserTokens() {
  removeItem(GI_TOKEN_NAME, '/', 'goibibo.com');
  removeItem(GA_TOKEN, '/', 'goibibo.com');
  removeItem(GI_UUID, '/', 'goibibo.com');
};
var getAuthToken = function getAuthToken() {
  return getItem('OAUTH-GOIBIBO');
};
var isUserTokenPresent = function isUserTokenPresent() {
  var authToken = getAuthToken();
  return !!authToken;
};
var getEncryptedUuid = function getEncryptedUuid() {
  return getItem('gapauthtoken');
};
var getUuidFromCookie = function getUuidFromCookie() {
  return getItem('gitokenid');
};
var setUserTokenAndUuid = function setUserTokenAndUuid(_ref) {
  var _window;
  var authToken = _ref.authToken,
    uuid = _ref.uuid;
  setItem(GI_TOKEN_NAME, authToken, {
    expires: 7776000,
    path: '/',
    domain: 'goibibo.com',
    secure: true
  });
  setItem(GA_TOKEN, (_window = window) == null ? void 0 : _window.btoa(uuid), {
    expires: 7776000,
    path: '/',
    domain: 'goibibo.com',
    secure: true
  });
  setItem(GI_UUID, uuid, {
    expires: 7776000,
    path: '/',
    domain: 'goibibo.com',
    secure: true
  });
};
var deleteSessionId = function deleteSessionId() {
  return removeItem(GI_SESSIONID, '/', 'goibibo.com');
};

var API_AUTH = {
  MWEB: 'h4nhc9jcgpAGIjp',
  DWEB: 'jkO7Ivpwsh4KaQ9',
  HCS: 'cmVzdDoxMjM0NTY='
};
var getUserIdentifierHeader = function getUserIdentifierHeader(_ref) {
  var _getBrowserDetails;
  var platform = _ref.platform;
  return JSON.stringify({
    type: 'auth',
    deviceId: visitorIdBrowser() || 'deviceId',
    os: platform === 'mweb' ? 'pwa' : 'desktop',
    osVersion: 'osVersion',
    appVersion: 'appVersion',
    imie: 'imie',
    ipAddress: 'ipAddress',
    timeZone: '+5.30 GMT',
    value: getAuthToken() || '',
    deviceOrBrowserInfo: ((_getBrowserDetails = getBrowserDetails()) == null ? void 0 : _getBrowserDetails.browserName) || ''
  });
};
var getEssentialHeader = function getEssentialHeader(_ref2) {
  var platform = _ref2.platform;
  return {
    Authorization: API_AUTH[platform.toUpperCase()],
    'User-Identifier': getUserIdentifierHeader({
      platform: platform
    }),
    'X-request-tracker': generateVid(),
    region: 'in',
    currency: 'inr',
    language: 'eng'
  };
};

var profileViewQuery = {
  query: [[{
    name: 'extendedUser',
    keys: ['profileId', 'uuid', 'primaryEmailId', 'userType', 'profileType', 'loginInfoList', 'personalDetails']
  }], [{
    name: 'extendedUser'
  }, {
    name: 'personalDetails',
    keys: ['gender', 'dateOfBirth', 'homeLocation']
  }], [{
    name: 'extendedUser'
  }, {
    name: 'personalDetails'
  }, {
    name: 'homeLocation',
    keys: ['cityCode', 'cityName', 'countryCode', 'countryName']
  }], [{
    name: 'extendedUser'
  }, {
    name: 'userImages',
    keys: ['fileUrl', 'imageClass'],
    filters: [{
      name: 'imageClass',
      type: 'string',
      value: 'displaypic'
    }]
  }], [{
    name: 'extendedUser'
  }, {
    name: 'profileCompletionDetails',
    keys: ['ctaDetails', 'completionScore', 'title', 'subText', 'component']
  }], [{
    name: 'loginInfoList',
    keys: ['loginId', 'loginType', 'primary', 'status', 'countryCode', 'verified', 'commId']
  }], [{
    name: 'miscFields',
    keys: ['altEmailId']
  }], [{
    name: 'extendedUser'
  }, {
    name: 'addressDetails',
    keys: ['addressId', 'city', 'name', 'category', 'houseNo', 'street', 'state', 'country', 'postalCode', 'landMark', 'status', 'lastUpdated', 'address1']
  }], [{
    name: 'extendedUser'
  }, {
    name: 'altVerifiedDetails',
    keys: ['loginId', 'loginType', 'status', 'ttl', 'programName']
  }]]
};
var extendedProfileViewQuery = {
  query: [[{
    name: 'extendedUser',
    keys: ['profileId', 'uuid', 'accountId', 'profileType', 'primaryEmailId', 'updatedAt', 'loginInfoList', 'personalDetails', 'corporateData']
  }], [{
    name: 'extendedUser'
  }, {
    name: 'addressDetails',
    keys: ['address1', 'postalCode', 'state', 'addressId', 'stateId'],
    filters: [{
      name: 'category',
      type: 'string',
      value: 'BILLING'
    }]
  }], [{
    name: 'extendedUser'
  }, {
    name: 'contactDetails',
    keys: ['contactId', 'type', 'category', 'name', 'info', 'additionalDetails', 'status'],
    filters: [{
      name: 'status',
      value: 'ACTIVE'
    }]
  }], [{
    name: 'personalDetails',
    keys: ['name', 'gender', 'childNCount', 'maritalStatus', 'anniversaryDate', 'dateOfBirth', 'nationality']
  }], [{
    name: 'extendedUser'
  }, {
    name: 'loginInfoList',
    keys: ['loginId', 'verified', 'status', 'loginType', 'commId', 'countryCode'],
    filters: [{
      name: 'verified',
      value: true
    }, {
      name: 'status',
      value: 'ACTIVE'
    }]
  }], [{
    name: 'extendedUser'
  }, {
    name: 'associatedTravellers',
    keys: ['travellerId', 'name', 'age', 'travellerEmail', 'status', 'nationality', 'dateOfBirth', 'gender', 'updatedAt', 'addressDetails', 'travelDocuments', 'relationship'],
    filters: [{
      name: 'status',
      value: 'ACTIVE'
    }]
  }], [{
    name: 'extendedUser'
  }, {
    name: 'associatedTravellers',
    filters: [{
      name: 'status',
      value: 'ACTIVE'
    }]
  }, {
    name: 'contactDetails',
    keys: ['contactId', 'countryCode', 'type', 'category', 'info', 'status'],
    filters: [{
      name: 'status',
      value: 'ACTIVE'
    }]
  }], [{
    name: 'extendedUser'
  }, {
    name: 'travelDocuments',
    keys: ['id', 'docType', 'docSubType', 'docNumber', 'name', 'docCountry', 'docCreatedDate', 'docExpiryDate', 'docIssuePlace', 'status'],
    filters: [{
      name: 'status',
      value: 'ACTIVE'
    }]
  }], [{
    name: 'extendedUser'
  }, {
    name: 'frequentFlyers',
    keys: ['airline', 'airlineName', 'ffNumber'],
    filters: [{
      name: 'status',
      value: 'ACTIVE'
    }]
  }], [{
    name: 'extendedUser'
  }, {
    name: 'contactDetails',
    keys: ['additionalDetails', 'category', 'info', 'type']
  }], [{
    name: 'extendedUser'
  }, {
    name: 'addressDetails',
    keys: ['city', 'state', 'country', 'address1', 'postalCode', 'category']
  }], [{
    name: 'extendedUser'
  }, {
    name: 'userImages',
    keys: ['fileName', 'filePath', 'id', 'status'],
    filters: [{
      name: 'status',
      value: 'ACTIVE'
    }]
  }], [{
    name: 'extendedUser'
  }, {
    name: 'associatedTravellers',
    filters: [{
      name: 'status',
      value: 'ACTIVE'
    }]
  }, {
    name: 'travelDocuments',
    keys: ['id', 'docType', 'docSubType', 'docNumber', 'name', 'docCountry', 'docCreatedDate', 'docExpiryDate', 'docIssuePlace', 'status'],
    filters: [{
      name: 'status',
      value: 'ACTIVE'
    }]
  }], [{
    name: 'travelDocuments',
    keys: ['id', 'docType', 'docNumber', 'docCountry', 'docExpiryDate', 'docIssuePlace'],
    filters: [{
      name: 'status',
      value: 'ACTIVE'
    }]
  }], [{
    name: 'associatedTravellers'
  }, {
    name: 'frequentFlyers',
    keys: ['airline', 'airlineName', 'ffNumber'],
    filters: [{
      name: 'status',
      value: 'ACTIVE'
    }]
  }], [{
    name: 'associatedTravellers'
  }, {
    name: 'userMiscInfo',
    keys: ['mealPref', 'seatPref', 'railPreferences']
  }], [{
    name: 'associatedTravellers'
  }, {
    name: 'cowinData',
    filters: [{
      name: 'isActive',
      type: 'bool',
      value: 'true'
    }]
  }], [{
    name: 'miscFields'
  }, {
    name: 'additionalPreferences',
    keys: ['domInsPreference', 'intInsPreference']
  }]]
};
var BILLING_CATEGORY = 'BILLING';

var fetchProfileDetails = function fetchProfileDetails(_ref) {
  var platform = _ref.platform,
    extendedDetails = _ref.extendedDetails;
  return fetchRequest(getApiUrls(platform).USER_DETAILS, {
    headers: getEssentialHeader({
      platform: platform
    }),
    body: extendedDetails ? extendedProfileViewQuery : profileViewQuery
  }).post();
};

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}

var generateProfileUpdateQuery = function generateProfileUpdateQuery(profileFields) {
  var firstName = profileFields.firstName,
    lastName = profileFields.lastName,
    dob = profileFields.dob,
    gender = profileFields.gender,
    email = profileFields.email,
    addressDetails = profileFields.addressDetails,
    homeLocation = profileFields.homeLocation;
  var q = {
    query: [[{
      name: 'extendedUser',
      values: {
        personalDetails: _extends({
          gender: gender,
          name: {
            firstName: firstName,
            lastName: lastName
          },
          dateOfBirth: dob
        }, homeLocation ? {
          homeLocation: homeLocation
        } : {}),
        miscFields: {
          altEmailId: email
        },
        addressDetails: addressDetails
      }
    }]]
  };
  /* remove all empty keys from query */
  return removeEmptyValues(q);
};
var getProfileUpdateQueryFromExtendedRes = function getProfileUpdateQueryFromExtendedRes(payload) {
  return {
    query: payload == null ? void 0 : payload.updateQuery
  };
};
var generateUserCreateQuery = function generateUserCreateQuery(fields) {
  var mobileNumber = fields.mobileNumber,
    fullName = fields.fullName,
    email = fields.email,
    _fields$countryCode = fields.countryCode,
    countryCode = _fields$countryCode === void 0 ? '91' : _fields$countryCode,
    gender = fields.gender,
    homeLocation = fields.homeLocation;
  var q = {
    query: [[{
      name: 'extendedUser',
      values: {
        profileType: 'PERSONAL',
        personalDetails: _extends({
          gender: gender,
          name: {
            firstName: fullName
          }
        }, homeLocation ? {
          homeLocation: homeLocation
        } : {}),
        miscFields: {
          altEmailId: email
        },
        loginInfoList: [{
          loginId: mobileNumber,
          countryCode: countryCode,
          loginType: 'MOBILE'
        }]
      }
    }]]
  };
  /* remove all empty keys from query */
  return removeEmptyValues(q);
};

var fetchProfileUpdate = function fetchProfileUpdate(_ref) {
  var platform = _ref.platform,
    data = _ref.data,
    extendedDetails = _ref.extendedDetails;
  return fetchRequest(getApiUrls(platform).UPDATE_PROFILE, {
    headers: getEssentialHeader({
      platform: platform
    }),
    body: extendedDetails ? getProfileUpdateQueryFromExtendedRes(data) : generateProfileUpdateQuery(data)
  }).post();
};

var fetchUpdateProfilePic = function fetchUpdateProfilePic(_ref) {
  var platform = _ref.platform,
    file = _ref.file;
  var formData = new FormData();
  formData.append('file', file);
  return fetchRequest(getApiUrls(platform).UPDATE_PROFILE_PIC, {
    headers: _extends({}, getEssentialHeader({
      platform: platform
    })),
    body: formData
  }).post();
};

var fetchSendOTP = function fetchSendOTP(_ref) {
  var platform = _ref.platform,
    mobileNumber = _ref.mobileNumber,
    loginType = _ref.loginType,
    _ref$countryCode = _ref.countryCode,
    countryCode = _ref$countryCode === void 0 ? 91 : _ref$countryCode,
    isEncoded = _ref.isEncoded;
  return fetchRequest(getApiUrls(platform).MOBILE_DETAILS, {
    headers: getEssentialHeader({
      platform: platform
    }),
    body: {
      loginId: mobileNumber,
      countryCode: countryCode,
      channel: [loginType],
      type: 6,
      isEncoded: isEncoded,
      appHashKey: platform === 'mweb' ? "@" + getBaseHostName() + " #" : ''
    },
    credentials: 'include'
  }).post();
};

var fetchCreateUser = function fetchCreateUser(opts) {
  var _ref = opts || {},
    platform = _ref.platform,
    transactionId = _ref.transactionId,
    mobileNumber = _ref.mobileNumber,
    fullName = _ref.fullName,
    email = _ref.email,
    countryCode = _ref.countryCode,
    gender = _ref.gender,
    homeLocation = _ref.homeLocation;
  return fetchRequest(getApiUrls(platform).CREATE_NEW_USER + "?guest=false&trxId=" + transactionId, {
    headers: getEssentialHeader({
      platform: platform
    }),
    body: generateUserCreateQuery({
      mobileNumber: mobileNumber,
      fullName: fullName,
      email: email,
      countryCode: countryCode,
      gender: gender,
      homeLocation: homeLocation
    })
  }).post();
};

var fetchLoginUser = function fetchLoginUser(opts) {
  var _ref = opts || {},
    platform = _ref.platform,
    mobileNumber = _ref.mobileNumber,
    passwordType = _ref.passwordType,
    countryCode = _ref.countryCode,
    password = _ref.password,
    isEncoded = _ref.isEncoded,
    encodedLoginId = _ref.encodedLoginId;
  return fetchRequest(getApiUrls(platform).LOGIN, {
    headers: getEssentialHeader({
      platform: platform
    }),
    body: {
      countryCode: countryCode || '91',
      mobileNumber: isEncoded ? encodedLoginId : mobileNumber,
      password: password,
      type: 'MOBILE',
      passwordType: passwordType,
      isEncoded: isEncoded,
      query: profileViewQuery.query
    }
  }).post();
};

var fetchSignupViaOTP = function fetchSignupViaOTP(opts) {
  var _ref = opts || {},
    platform = _ref.platform,
    mobileNumber = _ref.mobileNumber,
    _ref$countryCode = _ref.countryCode,
    countryCode = _ref$countryCode === void 0 ? 91 : _ref$countryCode,
    password = _ref.password;
  return fetchRequest(getApiUrls(platform).SIGN_UP, {
    headers: getEssentialHeader({
      platform: platform
    }),
    credentials: 'include',
    body: {
      mobileNumber: mobileNumber,
      countryCode: countryCode,
      type: '6',
      token: password
    }
  }).post();
};

var fetchLogoutUser = function fetchLogoutUser(_ref) {
  var platform = _ref.platform;
  return fetchRequest(getApiUrls(platform).LOGOUT, {
    headers: getEssentialHeader({
      platform: platform
    })
  })["delete"]().then(function (res) {
    if (res != null && res.success) {
      clearUserTokens();
    }
    return res;
  })["catch"](function (e) {
    return e;
  });
};

var fetchSendDeleteOTP = function fetchSendDeleteOTP(_ref) {
  var platform = _ref.platform;
  return fetchRequest(getApiUrls(platform).DELETE_OTP, {
    headers: getEssentialHeader({
      platform: platform
    }),
    body: {
      type: '16',
      channel: ['MOBILE']
    },
    credentials: 'include'
  }).post();
};

var TRUECALLER_DEEPLINK_DATA = {
  partnerKey: 'YDO2j919392e8b6fa4a059b697fd280503e67',
  partnerName: 'Goibibo',
  lang: 'en',
  privacyUrl: 'https://www.goibibo.com/info/privacy-policy/',
  termsUrl: 'https://www.goibibo.com/info/user-agreement/',
  loginPrefix: 'getstarted',
  loginSuffix: 'signin',
  btnShape: 'rect',
  skipOption: 'manualdetails',
  ctaPrefix: 'use',
  ctaColor: '%23ff6d38',
  ctaTextColor: '%23ffffff',
  PollingDefaultTimeout: 1000,
  PollingIntervalTimeout: {
    "default": 1000,
    stage1: 1500,
    stage2: 2500
  },
  PollingTimeoutCountSessionKey: 'truecaller_timer_count',
  PollingThreshold: 25,
  pollingHitStage: {
    stage1: 8,
    stage2: 15
  },
  skipCtaTruecallerActivate: 5,
  PollingState: {
    pending: 'PROCESSING',
    complete: 'SUCCESS',
    skipped: 'TRUE_CALLER_LOGIN_SKIPPED',
    error: 'error'
  }
};
var getTruecallerDeepLink = function getTruecallerDeepLink(reqId, deepLinkData) {
  return "truecallersdk://truesdk/web_verify?type=btmsheet&requestNonce=" + reqId + "&partnerKey=" + deepLinkData.partnerKey + "&partnerName=" + deepLinkData.partnerName + "&lang=" + deepLinkData.lang + "&skipOption=" + deepLinkData.skipOption + "&btnShape=" + deepLinkData.btnShape + "&loginPrefix=" + deepLinkData.loginPrefix + "&loginSuffix=" + deepLinkData.loginSuffix + "&ctaPrefix=" + deepLinkData.ctaPrefix + "&ctaColor=" + deepLinkData.ctaColor + "&ctaTextColor=" + deepLinkData.ctaTextColor + "&privacyUrl=" + deepLinkData.privacyUrl + "&termsUrl=" + deepLinkData.termsUrl;
};

var INVALID_AUTH_TOKEN_CODE = 5405;
var EXISTING_USER_STATUS_CODE = 4200;
var NEW_USER_STATUS_CODE = 4404;
var INVALID_OTP_CODE = 403;

// used on mweb and dweb both for login success event
var LOGIN_SUCCESS = 'LOGIN_SUCCESS';

var GOOGLE_RECAPTCHA_V2_CHECKBOX_SITE_KEY = '6LeEnmApAAAAAEfJiULRfVpgff8Nz0pQdUlRt6H4';

var fetchTruecallerLogin = function fetchTruecallerLogin(opts) {
  var platform = opts.platform,
    requestId = opts.requestId;
  return fetchRequest("" + getApiUrls(platform).SOCIAL_LOGIN, {
    headers: getEssentialHeader({
      platform: platform
    }),
    body: {
      accType: 'TRUE_CALLER',
      requestId: requestId,
      query: profileViewQuery.query
    }
  }).post();
};

var fetchVerifyEmail = function fetchVerifyEmail(_ref) {
  var platform = _ref.platform,
    token = _ref.token;
  return fetchRequest(getApiUrls(platform).VERIFY_EMAIL, {
    headers: getEssentialHeader({
      platform: platform
    }),
    body: {
      type: '2',
      token: token
    }
  }).post();
};

var fetchRequestEmailVerification = function fetchRequestEmailVerification(_ref) {
  var platform = _ref.platform,
    email = _ref.email;
  return fetchRequest(getApiUrls(platform).GET_EMAIL_TOKEN, {
    headers: getEssentialHeader({
      platform: platform
    }),
    body: {
      loginId: email,
      type: '2',
      channel: ['EMAIL']
    }
  }).post();
};

var fetchLoginJwtToken = function fetchLoginJwtToken(_ref) {
  var platform = _ref.platform,
    client = _ref.client;
  return fetchRequest(getApiUrls(platform).JWT_TOKEN, {
    headers: getEssentialHeader({
      platform: platform
    }),
    body: {
      client: client
    }
  }).post();
};

var fetchMultipleLogins = function fetchMultipleLogins(_ref) {
  var platform = _ref.platform;
  return fetchRequest(getApiUrls(platform).MULTIPLE_LOGINS, {
    headers: getEssentialHeader({
      platform: platform
    })
  }).post();
};

var logoutSpecificDevice = function logoutSpecificDevice(_ref) {
  var platform = _ref.platform,
    id = _ref.id;
  return fetchRequest(getApiUrls(platform).LOGOUT_DEVICE, {
    headers: getEssentialHeader({
      platform: platform
    }),
    query: "id=" + id
  })["delete"]();
};

var fetchRecaptchaVerification = function fetchRecaptchaVerification(_ref) {
  var platform = _ref.platform,
    recaptchaToken = _ref.recaptchaToken,
    deviceId = _ref.deviceId;
  return fetchRequest(getApiUrls(platform).RECAPTCHA_VERIFY, {
    headers: getEssentialHeader({
      platform: platform
    }),
    body: {
      deviceId: deviceId,
      action: ['RECAPTCHA_AUTH'],
      reCaptchaResp: recaptchaToken
    }
  }).post();
};

var fetchSendVerifyEmailOTP = function fetchSendVerifyEmailOTP(_ref) {
  var platform = _ref.platform,
    payload = _ref.payload,
    countryCode = _ref.countryCode,
    channelValue = _ref.channelValue;
  var request = {
    loginId: payload
  };
  if (countryCode) {
    request = _extends({}, request, {
      countryCode: countryCode
    });
  }
  if (channelValue) {
    request.channel = channelValue;
  }
  return fetchRequest(getApiUrls(platform).SEND_EMAIL_OTP, {
    headers: getEssentialHeader({
      platform: platform
    }),
    body: request,
    credentials: 'include'
  }).post();
};

var fetchVerifyEmailOTP = function fetchVerifyEmailOTP(_ref) {
  var _request;
  var platform = _ref.platform,
    payload = _ref.payload,
    idValue = _ref.idValue;
    _ref.idType;
    var countryCode = _ref.countryCode;
  var intVal = 7;
  var loginType = 'email';
  var request = (_request = {
    token: payload,
    type: intVal
  }, _request[loginType] = idValue, _request);
  if (countryCode) {
    request = _extends({}, request, {
      countryCode: countryCode
    });
  }
  return fetchRequest(getApiUrls(platform).VERIFY_EMAIL_OTP, {
    headers: getEssentialHeader({
      platform: platform
    }),
    body: request,
    credentials: 'include'
  }).post();
};

function _regeneratorRuntime() {
  _regeneratorRuntime = function () {
    return e;
  };
  var t,
    e = {},
    r = Object.prototype,
    n = r.hasOwnProperty,
    o = Object.defineProperty || function (t, e, r) {
      t[e] = r.value;
    },
    i = "function" == typeof Symbol ? Symbol : {},
    a = i.iterator || "@@iterator",
    c = i.asyncIterator || "@@asyncIterator",
    u = i.toStringTag || "@@toStringTag";
  function define(t, e, r) {
    return Object.defineProperty(t, e, {
      value: r,
      enumerable: !0,
      configurable: !0,
      writable: !0
    }), t[e];
  }
  try {
    define({}, "");
  } catch (t) {
    define = function (t, e, r) {
      return t[e] = r;
    };
  }
  function wrap(t, e, r, n) {
    var i = e && e.prototype instanceof Generator ? e : Generator,
      a = Object.create(i.prototype),
      c = new Context(n || []);
    return o(a, "_invoke", {
      value: makeInvokeMethod(t, r, c)
    }), a;
  }
  function tryCatch(t, e, r) {
    try {
      return {
        type: "normal",
        arg: t.call(e, r)
      };
    } catch (t) {
      return {
        type: "throw",
        arg: t
      };
    }
  }
  e.wrap = wrap;
  var h = "suspendedStart",
    l = "suspendedYield",
    f = "executing",
    s = "completed",
    y = {};
  function Generator() {}
  function GeneratorFunction() {}
  function GeneratorFunctionPrototype() {}
  var p = {};
  define(p, a, function () {
    return this;
  });
  var d = Object.getPrototypeOf,
    v = d && d(d(values([])));
  v && v !== r && n.call(v, a) && (p = v);
  var g = GeneratorFunctionPrototype.prototype = Generator.prototype = Object.create(p);
  function defineIteratorMethods(t) {
    ["next", "throw", "return"].forEach(function (e) {
      define(t, e, function (t) {
        return this._invoke(e, t);
      });
    });
  }
  function AsyncIterator(t, e) {
    function invoke(r, o, i, a) {
      var c = tryCatch(t[r], t, o);
      if ("throw" !== c.type) {
        var u = c.arg,
          h = u.value;
        return h && "object" == typeof h && n.call(h, "__await") ? e.resolve(h.__await).then(function (t) {
          invoke("next", t, i, a);
        }, function (t) {
          invoke("throw", t, i, a);
        }) : e.resolve(h).then(function (t) {
          u.value = t, i(u);
        }, function (t) {
          return invoke("throw", t, i, a);
        });
      }
      a(c.arg);
    }
    var r;
    o(this, "_invoke", {
      value: function (t, n) {
        function callInvokeWithMethodAndArg() {
          return new e(function (e, r) {
            invoke(t, n, e, r);
          });
        }
        return r = r ? r.then(callInvokeWithMethodAndArg, callInvokeWithMethodAndArg) : callInvokeWithMethodAndArg();
      }
    });
  }
  function makeInvokeMethod(e, r, n) {
    var o = h;
    return function (i, a) {
      if (o === f) throw new Error("Generator is already running");
      if (o === s) {
        if ("throw" === i) throw a;
        return {
          value: t,
          done: !0
        };
      }
      for (n.method = i, n.arg = a;;) {
        var c = n.delegate;
        if (c) {
          var u = maybeInvokeDelegate(c, n);
          if (u) {
            if (u === y) continue;
            return u;
          }
        }
        if ("next" === n.method) n.sent = n._sent = n.arg;else if ("throw" === n.method) {
          if (o === h) throw o = s, n.arg;
          n.dispatchException(n.arg);
        } else "return" === n.method && n.abrupt("return", n.arg);
        o = f;
        var p = tryCatch(e, r, n);
        if ("normal" === p.type) {
          if (o = n.done ? s : l, p.arg === y) continue;
          return {
            value: p.arg,
            done: n.done
          };
        }
        "throw" === p.type && (o = s, n.method = "throw", n.arg = p.arg);
      }
    };
  }
  function maybeInvokeDelegate(e, r) {
    var n = r.method,
      o = e.iterator[n];
    if (o === t) return r.delegate = null, "throw" === n && e.iterator.return && (r.method = "return", r.arg = t, maybeInvokeDelegate(e, r), "throw" === r.method) || "return" !== n && (r.method = "throw", r.arg = new TypeError("The iterator does not provide a '" + n + "' method")), y;
    var i = tryCatch(o, e.iterator, r.arg);
    if ("throw" === i.type) return r.method = "throw", r.arg = i.arg, r.delegate = null, y;
    var a = i.arg;
    return a ? a.done ? (r[e.resultName] = a.value, r.next = e.nextLoc, "return" !== r.method && (r.method = "next", r.arg = t), r.delegate = null, y) : a : (r.method = "throw", r.arg = new TypeError("iterator result is not an object"), r.delegate = null, y);
  }
  function pushTryEntry(t) {
    var e = {
      tryLoc: t[0]
    };
    1 in t && (e.catchLoc = t[1]), 2 in t && (e.finallyLoc = t[2], e.afterLoc = t[3]), this.tryEntries.push(e);
  }
  function resetTryEntry(t) {
    var e = t.completion || {};
    e.type = "normal", delete e.arg, t.completion = e;
  }
  function Context(t) {
    this.tryEntries = [{
      tryLoc: "root"
    }], t.forEach(pushTryEntry, this), this.reset(!0);
  }
  function values(e) {
    if (e || "" === e) {
      var r = e[a];
      if (r) return r.call(e);
      if ("function" == typeof e.next) return e;
      if (!isNaN(e.length)) {
        var o = -1,
          i = function next() {
            for (; ++o < e.length;) if (n.call(e, o)) return next.value = e[o], next.done = !1, next;
            return next.value = t, next.done = !0, next;
          };
        return i.next = i;
      }
    }
    throw new TypeError(typeof e + " is not iterable");
  }
  return GeneratorFunction.prototype = GeneratorFunctionPrototype, o(g, "constructor", {
    value: GeneratorFunctionPrototype,
    configurable: !0
  }), o(GeneratorFunctionPrototype, "constructor", {
    value: GeneratorFunction,
    configurable: !0
  }), GeneratorFunction.displayName = define(GeneratorFunctionPrototype, u, "GeneratorFunction"), e.isGeneratorFunction = function (t) {
    var e = "function" == typeof t && t.constructor;
    return !!e && (e === GeneratorFunction || "GeneratorFunction" === (e.displayName || e.name));
  }, e.mark = function (t) {
    return Object.setPrototypeOf ? Object.setPrototypeOf(t, GeneratorFunctionPrototype) : (t.__proto__ = GeneratorFunctionPrototype, define(t, u, "GeneratorFunction")), t.prototype = Object.create(g), t;
  }, e.awrap = function (t) {
    return {
      __await: t
    };
  }, defineIteratorMethods(AsyncIterator.prototype), define(AsyncIterator.prototype, c, function () {
    return this;
  }), e.AsyncIterator = AsyncIterator, e.async = function (t, r, n, o, i) {
    void 0 === i && (i = Promise);
    var a = new AsyncIterator(wrap(t, r, n, o), i);
    return e.isGeneratorFunction(r) ? a : a.next().then(function (t) {
      return t.done ? t.value : a.next();
    });
  }, defineIteratorMethods(g), define(g, u, "Generator"), define(g, a, function () {
    return this;
  }), define(g, "toString", function () {
    return "[object Generator]";
  }), e.keys = function (t) {
    var e = Object(t),
      r = [];
    for (var n in e) r.push(n);
    return r.reverse(), function next() {
      for (; r.length;) {
        var t = r.pop();
        if (t in e) return next.value = t, next.done = !1, next;
      }
      return next.done = !0, next;
    };
  }, e.values = values, Context.prototype = {
    constructor: Context,
    reset: function (e) {
      if (this.prev = 0, this.next = 0, this.sent = this._sent = t, this.done = !1, this.delegate = null, this.method = "next", this.arg = t, this.tryEntries.forEach(resetTryEntry), !e) for (var r in this) "t" === r.charAt(0) && n.call(this, r) && !isNaN(+r.slice(1)) && (this[r] = t);
    },
    stop: function () {
      this.done = !0;
      var t = this.tryEntries[0].completion;
      if ("throw" === t.type) throw t.arg;
      return this.rval;
    },
    dispatchException: function (e) {
      if (this.done) throw e;
      var r = this;
      function handle(n, o) {
        return a.type = "throw", a.arg = e, r.next = n, o && (r.method = "next", r.arg = t), !!o;
      }
      for (var o = this.tryEntries.length - 1; o >= 0; --o) {
        var i = this.tryEntries[o],
          a = i.completion;
        if ("root" === i.tryLoc) return handle("end");
        if (i.tryLoc <= this.prev) {
          var c = n.call(i, "catchLoc"),
            u = n.call(i, "finallyLoc");
          if (c && u) {
            if (this.prev < i.catchLoc) return handle(i.catchLoc, !0);
            if (this.prev < i.finallyLoc) return handle(i.finallyLoc);
          } else if (c) {
            if (this.prev < i.catchLoc) return handle(i.catchLoc, !0);
          } else {
            if (!u) throw new Error("try statement without catch or finally");
            if (this.prev < i.finallyLoc) return handle(i.finallyLoc);
          }
        }
      }
    },
    abrupt: function (t, e) {
      for (var r = this.tryEntries.length - 1; r >= 0; --r) {
        var o = this.tryEntries[r];
        if (o.tryLoc <= this.prev && n.call(o, "finallyLoc") && this.prev < o.finallyLoc) {
          var i = o;
          break;
        }
      }
      i && ("break" === t || "continue" === t) && i.tryLoc <= e && e <= i.finallyLoc && (i = null);
      var a = i ? i.completion : {};
      return a.type = t, a.arg = e, i ? (this.method = "next", this.next = i.finallyLoc, y) : this.complete(a);
    },
    complete: function (t, e) {
      if ("throw" === t.type) throw t.arg;
      return "break" === t.type || "continue" === t.type ? this.next = t.arg : "return" === t.type ? (this.rval = this.arg = t.arg, this.method = "return", this.next = "end") : "normal" === t.type && e && (this.next = e), y;
    },
    finish: function (t) {
      for (var e = this.tryEntries.length - 1; e >= 0; --e) {
        var r = this.tryEntries[e];
        if (r.finallyLoc === t) return this.complete(r.completion, r.afterLoc), resetTryEntry(r), y;
      }
    },
    catch: function (t) {
      for (var e = this.tryEntries.length - 1; e >= 0; --e) {
        var r = this.tryEntries[e];
        if (r.tryLoc === t) {
          var n = r.completion;
          if ("throw" === n.type) {
            var o = n.arg;
            resetTryEntry(r);
          }
          return o;
        }
      }
      throw new Error("illegal catch attempt");
    },
    delegateYield: function (e, r, n) {
      return this.delegate = {
        iterator: values(e),
        resultName: r,
        nextLoc: n
      }, "next" === this.method && (this.arg = t), y;
    }
  }, e;
}

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
  try {
    var info = gen[key](arg);
    var value = info.value;
  } catch (error) {
    reject(error);
    return;
  }
  if (info.done) {
    resolve(value);
  } else {
    Promise.resolve(value).then(_next, _throw);
  }
}
function _asyncToGenerator(fn) {
  return function () {
    var self = this,
      args = arguments;
    return new Promise(function (resolve, reject) {
      var gen = fn.apply(self, args);
      function _next(value) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
      }
      function _throw(err) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
      }
      _next(undefined);
    });
  };
}

var fetchFun$7 = /*#__PURE__*/function () {
  var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee(_ref) {
    var platform, data, res, _ref3, extendedUser, value;
    return _regeneratorRuntime().wrap(function _callee$(_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          platform = _ref.platform, data = _ref.data, _ref.onError;
          _context.next = 3;
          return fetchProfileUpdate({
            platform: platform,
            data: data
          });
        case 3:
          res = _context.sent;
          _context.next = 6;
          return promiseDelay(1000);
        case 6:
          _ref3 = (res == null ? void 0 : res.result) || {}, extendedUser = _ref3.extendedUser;
          value = extendedUser;
          return _context.abrupt("return", value);
        case 9:
        case "end":
          return _context.stop();
      }
    }, _callee);
  }));
  return function fetchFun(_x) {
    return _ref2.apply(this, arguments);
  };
}();
var useProfileUpdateStore = create(function (set) {
  return _extends({}, DEFAULT_STATE, {
    data: {},
    fetch: function fetch(opts) {
      return fetchSetter({
        set: set,
        fetchFun: fetchFun$7.bind(null, opts),
        onSuccess: opts == null ? void 0 : opts.onSuccess,
        onError: opts == null ? void 0 : opts.onError
      });
    }
  });
});

var storeUserInfoInClient = function storeUserInfoInClient(key, value) {
  var _extends2;
  var getExistingStorageData = docLocalStorage.getItem('userInfo') || {};
  var updateData = _extends({}, getExistingStorageData, (_extends2 = {}, _extends2[key] = value, _extends2));
  docLocalStorage.setItem('userInfo', updateData);
};
var removeUserInfoInClient = function removeUserInfoInClient() {
  var getExistingStorageData = docLocalStorage.getItem('userInfo');
  if (getExistingStorageData) {
    docLocalStorage.removeItem('userInfo');
  }
};
var fetchFun$6 = /*#__PURE__*/function () {
  var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee(_ref) {
    var _userImages$;
    var platform, res, _ref3, extendedUser, personalDetails, miscFields, userImages, uuid, profileId, profileType, profileCompletionDetails, loginInfoList, addressDetails, altVerifiedDetails, _ref4, gender, name, homeLocation, firstName, lastName, middleName, _ref5, _ref5$altEmailId, altEmailId, value;
    return _regeneratorRuntime().wrap(function _callee$(_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          platform = _ref.platform;
          _context.next = 3;
          return fetchProfileDetails({
            platform: platform
          });
        case 3:
          res = _context.sent;
          _ref3 = (res == null ? void 0 : res.result) || {}, extendedUser = _ref3.extendedUser;
          personalDetails = extendedUser.personalDetails, miscFields = extendedUser.miscFields, userImages = extendedUser.userImages, uuid = extendedUser.uuid, profileId = extendedUser.profileId, profileType = extendedUser.profileType, profileCompletionDetails = extendedUser.profileCompletionDetails, loginInfoList = extendedUser.loginInfoList, addressDetails = extendedUser.addressDetails, altVerifiedDetails = extendedUser.altVerifiedDetails;
          _ref4 = personalDetails || {}, gender = _ref4.gender, name = _ref4.name, homeLocation = _ref4.homeLocation;
          firstName = name.firstName, lastName = name.lastName, middleName = name.middleName;
          _ref5 = miscFields || {}, _ref5$altEmailId = _ref5.altEmailId, altEmailId = _ref5$altEmailId === void 0 ? '' : _ref5$altEmailId;
          value = {
            firstName: !firstName && !middleName && !lastName ? 'TRAVELLER' : firstName,
            middleName: middleName,
            lastName: lastName,
            mobileNumber: '',
            mobileVerified: '',
            mobileHash: '',
            email: '',
            emailVerified: '',
            emailHash: '',
            gender: gender,
            homeLocation: homeLocation,
            /* API gives epoc -> convert it to yyyy-mm-dd */
            dob: personalDetails.dateOfBirth ? epocToDate(personalDetails.dateOfBirth) : undefined,
            profileImage: userImages == null || (_userImages$ = userImages[0]) == null ? void 0 : _userImages$.fileUrl,
            uuid: uuid,
            profileId: profileId,
            profileType: profileType,
            countryCode: '',
            isLoggedIn: !!uuid,
            profileCompletionDetails: (profileCompletionDetails == null ? void 0 : profileCompletionDetails[0]) || {},
            addressDetails: addressDetails,
            altVerifiedDetails: altVerifiedDetails
          };
          loginInfoList.forEach(function (item) {
            if ((item == null ? void 0 : item.loginType) === 'MOBILE') {
              var _item$countryCode;
              value.mobileVerified = item == null ? void 0 : item.verified;
              value.countryCode = item == null ? void 0 : item.countryCode;
              value.mobileNumber = item.loginId.slice(item == null || (_item$countryCode = item.countryCode) == null ? void 0 : _item$countryCode.length);
              value.mobileHash = item == null ? void 0 : item.commId;
              if (isOnClient) storeUserInfoInClient('mHash', item == null ? void 0 : item.commId);
            }
            if (item.loginType === 'EMAIL') {
              value.emailVerified = item.verified;
              value.email = item.loginId;
              value.emailHash = item.commId;
              if (isOnClient) storeUserInfoInClient('eHash', item == null ? void 0 : item.commId);
            }
          });
          if (!value.email) {
            value.email = altEmailId;
          }
          return _context.abrupt("return", value);
        case 13:
        case "end":
          return _context.stop();
      }
    }, _callee);
  }));
  return function fetchFun(_x) {
    return _ref2.apply(this, arguments);
  };
}();
var useProfileStore = create()(function (set) {
  return _extends({}, DEFAULT_STATE, {
    fetch: function fetch(opts) {
      return fetchSetter({
        set: set,
        fetchFun: fetchFun$6.bind(null, opts),
        onError: function onError(err) {
          var res = err == null ? void 0 : err.response;
          if ((res == null ? void 0 : res.code) === 5405) {
            removeUserInfoInClient();
          }
          opts == null || opts.onError({
            code: res == null ? void 0 : res.code,
            message: res == null ? void 0 : res.message,
            status: err == null ? void 0 : err.status
          });
        }
      });
    }
  });
});

var fetchFun$5 = /*#__PURE__*/function () {
  var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee(_ref) {
    var platform, file, res, _ref3, data, success, message;
    return _regeneratorRuntime().wrap(function _callee$(_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          platform = _ref.platform, file = _ref.file;
          _context.next = 3;
          return fetchUpdateProfilePic({
            platform: platform,
            file: file
          });
        case 3:
          res = _context.sent;
          _ref3 = res || {}, data = _ref3.data, success = _ref3.success, message = _ref3.message;
          if (success) {
            _context.next = 7;
            break;
          }
          return _context.abrupt("return", {
            error: true,
            errorMsg: message
          });
        case 7:
          return _context.abrupt("return", {
            error: false,
            uploadedImage: data
          });
        case 8:
        case "end":
          return _context.stop();
      }
    }, _callee);
  }));
  return function fetchFun(_x) {
    return _ref2.apply(this, arguments);
  };
}();
var useUploadProfileImageStore = create(function (set) {
  return _extends({}, DEFAULT_STATE, {
    data: {
      uploadedImage: '',
      successMsg: '',
      error: false,
      errorMsg: ''
    },
    fetch: function fetch(opts) {
      return fetchSetter({
        set: set,
        fetchFun: fetchFun$5.bind(null, opts),
        onSuccess: opts == null ? void 0 : opts.onSuccess
      });
    }
  });
});

var loginWithMobileErrors = {
  typeErrorMsg: 'Alphabets and Special Characters are not allowed',
  notAValidMobileNumber: 'Please enter a valid mobile number',
  lengthErrorMsg: 'Please enter a 10 digit mobile number',
  intlMobileLengthErrorMsg: 'Please enter a minimum 6 digit mobile number'
};
var MOBILE_NUMBER_REGEX = /^[6-9]\d{9}$/;
var ONLY_DIGITS_REGEX = /^\d+$/;
var MOBILE_NUMBER_MAX_LENGTH = 10;
var INTL_MOBILE_NUMBER_MIN_LENGTH = 6;
var mobileValidation = function mobileValidation(phone, isInternational) {
  if (phone) {
    if (Number.isNaN(Number(phone))) {
      return {
        valid: false,
        errorMsg: loginWithMobileErrors.typeErrorMsg
      };
    }
    if (isInternational) {
      if (phone.length < INTL_MOBILE_NUMBER_MIN_LENGTH) {
        return {
          valid: false,
          errorMsg: loginWithMobileErrors.intlMobileLengthErrorMsg
        };
      }
      if (!ONLY_DIGITS_REGEX.test(phone)) {
        return {
          valid: false,
          errorMsg: loginWithMobileErrors.typeErrorMsg
        };
      }
      return {
        valid: true,
        errorMsg: ''
      };
    }
    if (typeof phone === 'string') {
      if (phone.length !== MOBILE_NUMBER_MAX_LENGTH) {
        return {
          valid: false,
          errorMsg: loginWithMobileErrors.lengthErrorMsg
        };
      }
    }
    if (MOBILE_NUMBER_REGEX.test(phone)) {
      return {
        valid: true,
        errorMsg: ''
      };
    }
  }
  return {
    valid: false,
    errorMsg: loginWithMobileErrors.notAValidMobileNumber
  };
};
var isMobileEncoded = function isMobileEncoded(mobileNumber, encodedMobileNumber) {
  if (mobileNumber != null && mobileNumber.includes('**') && encodedMobileNumber === mobileNumber) return true;
  return false;
};

var fetchFun$4 = /*#__PURE__*/function () {
  var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee(_ref) {
    var platform, data, res;
    return _regeneratorRuntime().wrap(function _callee$(_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          platform = _ref.platform, data = _ref.data;
          _context.next = 3;
          return fetchSendOTP(_extends({
            platform: platform
          }, data, {
            mobileNumber: data.isEncoded ? data.encodedLoginId : data.mobileNumber
          }));
        case 3:
          res = _context.sent;
          return _context.abrupt("return", _extends({}, res, {
            mobileNumber: data.mobileNumber,
            countryCode: data.countryCode,
            isMobileNumberEncoded: data.isEncoded || false
          }));
        case 5:
        case "end":
          return _context.stop();
      }
    }, _callee);
  }));
  return function fetchFun(_x) {
    return _ref2.apply(this, arguments);
  };
}();
var intialState = {
  validation: {
    isValidMobile: false,
    message: ''
  },
  error: {
    errorMsg: '',
    errorDetails: null
  }
};
var useSendOtpStore = create(function (set) {
  return _extends({}, DEFAULT_STATE, {
    data: _extends({}, intialState),
    fetch: function fetch(opts) {
      return fetchSetter({
        set: set,
        fetchFun: fetchFun$4.bind(null, opts),
        onSuccess: function onSuccess(res) {
          if (res.success) {
            if (opts.onSuccess) opts.onSuccess(res);
          } else {
            set(function (s) {
              return {
                error: true,
                data: _extends({}, s.data, {
                  error: {
                    errorMsg: res.message
                  }
                })
              };
            });
            if (opts.onError) opts.onError();
          }
        },
        onError: function onError(err) {
          var _err$response;
          var message = 'Some error occurred, please try again.';
          if ((err == null ? void 0 : err.status) === 400) message = err == null || (_err$response = err.response) == null ? void 0 : _err$response.message;
          set(function (s) {
            return {
              error: true,
              data: _extends({}, s.data, {
                error: {
                  errorMsg: message,
                  errorDetails: err
                }
              })
            };
          });
          if (opts.onError) opts.onError();
        }
      });
    },
    checkValid: function checkValid(mobileNumber, isMobileNumberEncoded, isInternational) {
      var validDetails = _extends({}, intialState.validation);
      if (isMobileNumberEncoded) {
        validDetails.isValidMobile = true;
      } else {
        var data = mobileValidation(mobileNumber, isInternational);
        validDetails.isValidMobile = data.valid;
        validDetails.message = data.errorMsg;
      }
      set({
        data: {
          validation: validDetails
        }
      });
      return validDetails.isValidMobile;
    }
  });
});
var clearSendOtpStore = function clearSendOtpStore() {
  return useSendOtpStore.setState({
    data: _extends({}, intialState),
    error: false
  });
};

var fetchFun$3 = /*#__PURE__*/function () {
  var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee(_ref) {
    var _resData$profileLogin;
    var platform, data, res, _ref3, resData, code, message, profileDetails, authToken, _profileDetails$userD, userDetails, _ref4, uuid;
    return _regeneratorRuntime().wrap(function _callee$(_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          platform = _ref.platform, data = _ref.data;
          _context.next = 3;
          return fetchLoginUser(_extends({
            platform: platform
          }, data));
        case 3:
          res = _context.sent;
          _ref3 = res || {}, resData = _ref3.data, code = _ref3.code, message = _ref3.message;
          /* Error case */
          if (!code) {
            _context.next = 7;
            break;
          }
          throw {
            code: code,
            message: message
          };
        case 7:
          profileDetails = (resData == null || (_resData$profileLogin = resData.profileLoginResponse) == null ? void 0 : _resData$profileLogin[0]) || {};
          authToken = profileDetails.authToken, _profileDetails$userD = profileDetails.userDetails, userDetails = _profileDetails$userD === void 0 ? {} : _profileDetails$userD;
          _ref4 = userDetails.extendedUser || {}, uuid = _ref4.uuid;
          if (authToken && uuid) {
            setUserTokenAndUuid({
              authToken: authToken,
              uuid: uuid
            });
          }
          return _context.abrupt("return", res);
        case 12:
        case "end":
          return _context.stop();
      }
    }, _callee);
  }));
  return function fetchFun(_x) {
    return _ref2.apply(this, arguments);
  };
}();
var useLoginUserStore = create(function (set) {
  return _extends({}, DEFAULT_STATE, {
    data: {},
    fetch: function fetch(opts) {
      return fetchSetter({
        set: set,
        fetchFun: fetchFun$3.bind(null, opts),
        onSuccess: function onSuccess(res) {
          return opts == null ? void 0 : opts.onSuccess(res);
        },
        onError: function onError(err) {
          var res = err == null ? void 0 : err.response;
          opts == null || opts.onError({
            code: res == null ? void 0 : res.code,
            message: res == null ? void 0 : res.message
          });
        }
      });
    }
  });
});

var fetchFun$2 = /*#__PURE__*/function () {
  var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee(_ref) {
    var platform, data, res, _res$data, trxID;
    return _regeneratorRuntime().wrap(function _callee$(_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          platform = _ref.platform, data = _ref.data, _ref.onError;
          _context.next = 3;
          return fetchSignupViaOTP(_extends({
            platform: platform
          }, data));
        case 3:
          res = _context.sent;
          if (!res.success) {
            _context.next = 7;
            break;
          }
          trxID = (_res$data = res.data) == null ? void 0 : _res$data.trxId;
          return _context.abrupt("return", {
            transactionId: trxID
          });
        case 7:
          if (!res.code) {
            _context.next = 9;
            break;
          }
          throw _extends({}, res);
        case 9:
          return _context.abrupt("return", {});
        case 10:
        case "end":
          return _context.stop();
      }
    }, _callee);
  }));
  return function fetchFun(_x) {
    return _ref2.apply(this, arguments);
  };
}();
var useSignupViaOTPStore = create(function (set) {
  return _extends({}, DEFAULT_STATE, {
    data: {},
    fetch: function fetch(opts) {
      return fetchSetter({
        set: set,
        fetchFun: fetchFun$2.bind(null, opts),
        onSuccess: function onSuccess(res) {
          return opts == null ? void 0 : opts.onSuccess(res.trxID);
        },
        onError: function onError(err) {
          var res = err == null ? void 0 : err.response;
          opts == null || opts.onError({
            code: res == null ? void 0 : res.code,
            message: res == null ? void 0 : res.message
          });
        }
      });
    },
    createUser: function createUser(opts) {
      return fetchCreateUser(opts);
    }
  });
});

var fetchFun$1 = /*#__PURE__*/function () {
  var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee(_ref) {
    var platform, data, res, _ref3, resData, message, _resData$profileLogin, profileDetails, authToken, _profileDetails$userD, userDetails, _ref4, uuid;
    return _regeneratorRuntime().wrap(function _callee$(_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          platform = _ref.platform, data = _ref.data;
          _context.next = 3;
          return fetchTruecallerLogin(_extends({
            platform: platform
          }, data));
        case 3:
          res = _context.sent;
          _ref3 = res || {}, resData = _ref3.data, _ref3.code, message = _ref3.message;
          if (message === 'SUCCESS') {
            profileDetails = (resData == null || (_resData$profileLogin = resData.profileLoginResponse) == null ? void 0 : _resData$profileLogin[0]) || {};
            authToken = profileDetails.authToken, _profileDetails$userD = profileDetails.userDetails, userDetails = _profileDetails$userD === void 0 ? {} : _profileDetails$userD;
            _ref4 = userDetails.extendedUser || {}, uuid = _ref4.uuid;
            if (authToken && uuid) {
              setUserTokenAndUuid({
                authToken: authToken,
                uuid: uuid
              });
            }
          }
          return _context.abrupt("return", res);
        case 7:
        case "end":
          return _context.stop();
      }
    }, _callee);
  }));
  return function fetchFun(_x) {
    return _ref2.apply(this, arguments);
  };
}();
var useTruecallerLoginStore = create(function (set) {
  return _extends({}, DEFAULT_STATE, {
    data: {},
    fetch: function fetch(opts) {
      return fetchSetter({
        set: set,
        fetchFun: fetchFun$1.bind(null, opts),
        onSuccess: function onSuccess(details) {
          return opts == null ? void 0 : opts.onSuccess(details);
        },
        onError: function onError(err) {
          var res = err == null ? void 0 : err.response;
          opts == null || opts.onError({
            code: res == null ? void 0 : res.code,
            message: res == null ? void 0 : res.message
          });
        }
      });
    }
  });
});

var fetchFun = /*#__PURE__*/function () {
  var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee(_ref) {
    var platform, client;
    return _regeneratorRuntime().wrap(function _callee$(_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          platform = _ref.platform, client = _ref.client;
          return _context.abrupt("return", fetchLoginJwtToken({
            platform: platform,
            client: client
          }));
        case 2:
        case "end":
          return _context.stop();
      }
    }, _callee);
  }));
  return function fetchFun(_x) {
    return _ref2.apply(this, arguments);
  };
}();
var useLoginEncryptedJwtToken = create(function (set) {
  return _extends({}, DEFAULT_STATE, {
    data: {},
    fetch: function fetch(opts) {
      return fetchSetter({
        set: set,
        fetchFun: fetchFun.bind(null, opts),
        onError: opts.onError,
        onSuccess: opts.onSuccess
      });
    }
  });
});

var fetchPrefillLoginDetails = function fetchPrefillLoginDetails(_ref) {
  var platform = _ref.platform,
    controller = _ref.controller;
  return fetchRequest(getApiUrls(platform).PREFILL_DETAILS, {
    headers: getEssentialHeader({
      platform: platform
    }),
    body: {
      retryCount: 1
    },
    controller: controller
  }).post();
};

var _fetchFun = /*#__PURE__*/function () {
  var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee(_ref) {
    var platform, controller, res;
    return _regeneratorRuntime().wrap(function _callee$(_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          platform = _ref.platform, controller = _ref.controller;
          _context.next = 3;
          return fetchPrefillLoginDetails({
            platform: platform,
            controller: controller
          });
        case 3:
          res = _context.sent;
          return _context.abrupt("return", {
            encodedMobileNumber: res == null ? void 0 : res.loginId,
            countryCode: res == null ? void 0 : res.countryCode,
            encLoginId: res == null ? void 0 : res.encLoginId
          });
        case 5:
        case "end":
          return _context.stop();
      }
    }, _callee);
  }));
  return function fetchFun(_x) {
    return _ref2.apply(this, arguments);
  };
}();
var usePrefillLoginDetails = create(function (set) {
  var controller = typeof AbortController === 'function' ? new AbortController() : undefined;
  return _extends({}, DEFAULT_STATE, {
    controller: controller,
    userInputMobileNumber: null,
    fetch: function fetch(opts) {
      return fetchSetter({
        set: set,
        fetchFun: function fetchFun() {
          return _fetchFun(_extends({}, opts, {
            controller: controller
          }));
        }
      });
    },
    setUserInputMobileNumber: function setUserInputMobileNumber(_ref3) {
      var mobile = _ref3.mobile,
        countryCode = _ref3.countryCode;
      return set({
        userInputMobileNumber: {
          mobile: mobile,
          countryCode: countryCode
        }
      });
    }
  });
});

var EMAIL_REGEX = /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i;
var isEmailValid = function isEmailValid(emailId) {
  if (emailId) return EMAIL_REGEX.test(emailId);
  return false;
};

var ALPHABET_REGEX = /^[a-zA-Z_]+( [a-zA-Z_]+)*$/;
var isNameValid = function isNameValid(fullName) {
  if (fullName) return ALPHABET_REGEX.test(fullName);
  return false;
};

var NUMREGEX = /^[0-9]+$/;
var isNumberValid = function isNumberValid(num) {
  if (num) return NUMREGEX.test(num);
  return false;
};

export { ALPHABET_REGEX, BILLING_CATEGORY, EMAIL_REGEX, EXISTING_USER_STATUS_CODE, GOOGLE_RECAPTCHA_V2_CHECKBOX_SITE_KEY, INTL_MOBILE_NUMBER_MIN_LENGTH, INVALID_AUTH_TOKEN_CODE, INVALID_OTP_CODE, LOGIN_SUCCESS, MOBILE_NUMBER_MAX_LENGTH, NEW_USER_STATUS_CODE, TRUECALLER_DEEPLINK_DATA, clearSendOtpStore, clearUserTokens, deleteSessionId, extendedProfileViewQuery, fetchCreateUser, fetchLoginJwtToken, fetchLoginUser, fetchLogoutUser, fetchMultipleLogins, fetchProfileDetails, fetchProfileUpdate, fetchRecaptchaVerification, fetchRequestEmailVerification, fetchSendDeleteOTP, fetchSendOTP, fetchSendVerifyEmailOTP, fetchSignupViaOTP, fetchTruecallerLogin, fetchUpdateProfilePic, fetchVerifyEmail, fetchVerifyEmailOTP, getAuthToken, getEncryptedUuid, getEssentialHeader, getTruecallerDeepLink, getUuidFromCookie, isEmailValid, isMobileEncoded, isNameValid, isNumberValid, isUserTokenPresent, logoutSpecificDevice, mobileValidation, profileViewQuery, setUserTokenAndUuid, useLoginEncryptedJwtToken, useLoginUserStore, usePrefillLoginDetails, useProfileStore, useProfileUpdateStore, useSendOtpStore, useSignupViaOTPStore, useTruecallerLoginStore, useUploadProfileImageStore };
